import { Box, Button, Heading, Spinner, VStack } from "@chakra-ui/react";
import { SignOutButton, useClerk, useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { useAppState } from "~/hooks/useAppState";

type PrivateRouteProps = {
  children: React.ReactNode;
  redirectTo?: string;
};

const RequireAuth = ({ children, redirectTo = "/login" }: PrivateRouteProps) => {
  const clerk = useClerk();
  const user = useUser();
  const [hasCheckedUserInfo, setHasCheckedUserInfo] = useState(false);
  const { userInfo, setUserInfo, setHasOrg } = useAppState("userInfo", "setUserInfo", "setHasOrg");
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    if (!clerk.loaded) {
      return;
    }
    if (!user.isSignedIn) {
      setHasCheckedUserInfo(true);
      return;
    }

    if (user.isSignedIn && userInfo) {
      setHasCheckedUserInfo(true);

      const memberships = user.user?.organizationMemberships || [];
      const firstOrganization = memberships.length > 0 ? memberships[0]?.organization : null;

      setHasOrg(!!firstOrganization);

      // Only try to set organization if:
      // 1. User has memberships
      // 2. Organization exists
      // 3. It's different from current active org
      // 4. We haven't already set it (avoid loops)
      if (firstOrganization && memberships.length > 0 && clerk.organization?.id !== firstOrganization.id) {
        // Use the organization ID string, not the object
        clerk.setActive({ organization: firstOrganization.id }).catch((error) => {
          // Silently handle error - user might not have access
          console.warn("Could not set active organization:", error.message || error);
        });
      }
      // Don't try to clear organization if user has none - let Clerk handle it
    }

    if (user.isSignedIn && !userInfo) {
      const fetchUserInfo = async () => {
        try {
          const response = await fetch(`api/user-info`);

          if (response.status === 200) {
            const data = await response.json();
            setUserInfo(data);
            setHasCheckedUserInfo(true);
            setIsAuthorized(true);
          } else if (response.status === 403) {
            setUserInfo(undefined);
            setHasCheckedUserInfo(true);
            setIsAuthorized(false);
          }
        } catch (error) {
          console.error("Error fetching user info:", error);
          setHasCheckedUserInfo(true);
          setIsAuthorized(false);
        }
      };
      fetchUserInfo();
    }
  }, [
    clerk.loaded,
    clerk.organization?.id,
    user.isSignedIn,
    userInfo,
    clerk,
    setUserInfo,
    user.user?.organizationMemberships,
    setHasOrg
  ]);

  const isAuthenticated = user.isSignedIn;

  if (!hasCheckedUserInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (isAuthenticated && !isAuthorized && hasCheckedUserInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" width="100vw">
        <VStack>
          <Heading as="h1">Unauthorized</Heading>
          <Heading as="h2" size="sm">
            Sorry, {user.user?.primaryEmailAddress?.emailAddress} is not authorized to view this page
          </Heading>
          <Button variant="secondary" size="sm">
            <SignOutButton />
          </Button>
        </VStack>
      </Box>
    );
  }

  return isAuthenticated ? (children as React.ReactElement) : <Navigate to={redirectTo} />;
};

export default RequireAuth;
